import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import api from '../config/api';

const AnalyticsContext = createContext();

export const AnalyticsProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [isTrackingEnabled, setIsTrackingEnabled] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Online/offline durumunu takip et
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Offline durumda biriken eventleri gönder
      sendPendingEvents();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Analytics'i başlat
  const initializeAnalytics = useCallback(async (userId = null) => {
    try {
      if (isInitialized) return;

      // Local storage'dan session ID al veya yeni oluştur
      let currentSessionId = localStorage.getItem('analytics_session_id');
      if (!currentSessionId) {
        currentSessionId = uuidv4();
        localStorage.setItem('analytics_session_id', currentSessionId);
      }

      setSessionId(currentSessionId);

      // Backend'e session oluştur/al
      const response = await api.post('/analytics/track/session', {
        sessionId: currentSessionId,
        userId: userId
      });

      if (response.data.success) {
        console.log('📊 Analytics initialized:', response.data.data);
        setIsInitialized(true);
        
        // Biriken eventleri gönder
        sendPendingEvents();
      }

    } catch (error) {
      console.error('Analytics initialization error:', error);
      // Hata durumunda da session ID'yi set et
      setSessionId(localStorage.getItem('analytics_session_id') || uuidv4());
      setIsInitialized(true);
    }
  }, [isInitialized]);

  // Event gönder
  const sendEvent = useCallback(async (endpoint, data) => {
    try {
      if (!isTrackingEnabled || !sessionId) {
        // Tracking kapalıysa veya session yoksa eventi beklemede tut
        setPendingEvents(prev => [...prev, { endpoint, data, timestamp: Date.now() }]);
        return;
      }

      const response = await api.post(endpoint, {
        ...data,
        sessionId
      });

      if (response.data.success) {
        console.log(`📊 Event sent to ${endpoint}:`, data);
      }

    } catch (error) {
      console.error(`Event send error (${endpoint}):`, error);
      // Hata durumunda eventi beklemede tut
      setPendingEvents(prev => [...prev, { endpoint, data, timestamp: Date.now() }]);
    }
  }, [sessionId, isTrackingEnabled]);

  // Bekleyen eventleri gönder
  const sendPendingEvents = useCallback(async () => {
    if (pendingEvents.length === 0 || !isOnline) return;

    try {
      for (const event of pendingEvents) {
        await sendEvent(event.endpoint, event.data);
      }
      
      // Başarılı gönderimden sonra beklemedeki eventleri temizle
      setPendingEvents([]);
      console.log(`📊 Sent ${pendingEvents.length} pending events`);

    } catch (error) {
      console.error('Pending events send error:', error);
    }
  }, [pendingEvents, isOnline, sendEvent]);

  // Sayfa görüntüleme takip et
  const trackPageView = useCallback((pagePath, pageTitle, referrer) => {
    sendEvent('/analytics/track/page-view', {
      pagePath,
      pageTitle,
      referrer
    });
  }, [sendEvent]);

  // Ürün etkileşimi takip et (JSONB Format)
  const trackProductInteraction = useCallback((
    productId, 
    actionType, 
    productName, 
    productPrice,
    productCategory = null,
    productBrand = null,
    productImage = null,
    productUrl = null,
    additionalData = {}
  ) => {
    sendEvent('/analytics/track/product-interaction', {
      productId,
      actionType,
      productName,
      productPrice,
      productCategory,
      productBrand,
      productImage,
      productUrl,
      additionalData
    });
  }, [sendEvent]);

  // Koleksiyon etkileşimi takip et (JSONB Format)
  const trackCollectionInteraction = useCallback((
    collectionId, 
    actionType, 
    collectionName,
    collectionDescription = null,
    collectionImage = null,
    collectionUrl = null,
    productCount = null,
    additionalData = {}
  ) => {
    sendEvent('/analytics/track/collection-interaction', {
      collectionId,
      actionType,
      collectionName,
      collectionDescription,
      collectionImage,
      collectionUrl,
      productCount,
      additionalData
    });
  }, [sendEvent]);

  // Arama takip et (JSONB Format)
  const trackSearch = useCallback((
    searchTerm, 
    actionType = 'search',
    resultsCount = null,
    searchFilters = {},
    clickedResult = {},
    additionalData = {}
  ) => {
    sendEvent('/analytics/track/search', {
      searchTerm,
      actionType,
      resultsCount,
      searchFilters,
      clickedResult,
      additionalData
    });
  }, [sendEvent]);

  // Tracking'i aç/kapat
  const toggleTracking = useCallback((enabled) => {
    setIsTrackingEnabled(enabled);
    localStorage.setItem('analytics_tracking_enabled', enabled.toString());
  }, []);

  // Tracking durumunu local storage'dan yükle
  useEffect(() => {
    const savedTrackingState = localStorage.getItem('analytics_tracking_enabled');
    if (savedTrackingState !== null) {
      setIsTrackingEnabled(savedTrackingState === 'true');
    }
  }, []);

  // Sayfa kapatılırken son eventleri gönder
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pendingEvents.length > 0) {
        // Synchronous olarak gönder (navigator.sendBeacon kullanılabilir)
        pendingEvents.forEach(event => {
          try {
            navigator.sendBeacon(
              `${api.defaults.baseURL}${event.endpoint}`,
              JSON.stringify({ ...event.data, sessionId })
            );
          } catch (error) {
            console.error('Beacon send error:', error);
          }
        });
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [pendingEvents, sessionId]);

  const value = {
    // State
    sessionId,
    isTrackingEnabled,
    isInitialized,
    isOnline,
    pendingEventsCount: pendingEvents.length,

    // Actions
    initializeAnalytics,
    trackPageView,
    trackProductInteraction,
    trackCollectionInteraction,
    trackSearch,
    toggleTracking,
    sendPendingEvents
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export default AnalyticsContext;